import ScanBytePage from "./pages/ScanBytePage";
import { AppStoreButton, ArrowIcon } from "./components/Shared";

const APP_STORE_URL = "https://apps.apple.com/app/id6747396222";
const PRIVACY_URL = "https://taspace.top/legal/hdify/privacy/latest";
const TERMS_URL = "https://taspace.top/legal/hdify/terms/latest";
const SUPPORT_URL = "mailto:fingertipstudio@outlook.com";

const features = [
  {
    number: "01",
    title: "Enhance photos",
    body: "Bring out detail and clarity in everyday photos, portraits, and memories. Choose Photo or Anime mode to match your image.",
  },
  {
    number: "02",
    title: "Remove distractions",
    body: "Brush over unwanted people, signs, or small details. Adjust the brush, undo, redo, and process only the area you select.",
  },
  {
    number: "03",
    title: "Create clean cutouts",
    body: "Remove backgrounds for profile pictures, product photos, and creative projects. Keep transparency or choose a solid color.",
  },
];

function App() {
  // ScanByte has its own hostname; keep the old path as a temporary
  // compatibility route for already-shared links.
  if (window.location.hostname === "scanweb.taspace.top" || window.location.pathname.startsWith("/scanbyte")) {
    return <ScanBytePage />;
  }

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="HDify home">
          <img src="/assets/hdify/app-icon.webp" alt="" width="256" height="256" />
          <span>HDify</span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#features">Features</a>
          <a href="#privacy">Privacy</a>
          <a className="nav-download" href={APP_STORE_URL} target="_blank" rel="noreferrer">Get the app <ArrowIcon /></a>
        </nav>
      </header>

      <main id="top">
        <section className="hero section-wrap">
          <div className="hero-copy">
            <p className="eyebrow">Private AI photo editor</p>
            <h1>Make every photo<br /><span>worth keeping.</span></h1>
            <p className="hero-lede">Enhance details, erase distractions, and remove backgrounds—all on your device.</p>
            <div className="hero-actions">
              <AppStoreButton href={APP_STORE_URL} />
              <a className="text-link" href="#features">See what it can do <span aria-hidden="true">↓</span></a>
            </div>
            <ul className="trust-list" aria-label="Product benefits">
              <li>On-device</li>
              <li>No account</li>
              <li>Photos not uploaded</li>
            </ul>
          </div>

          <div className="hero-visual" aria-label="Photo enhancement before and after example">
            <div className="photo-stack photo-stack--back" aria-hidden="true"></div>
            <div className="comparison-card">
              <div className="comparison-half comparison-before">
                <img src="/assets/hdify/enhance-before.webp" alt="Portrait before enhancement" width="280" height="350" fetchPriority="high" />
                <span>Before</span>
              </div>
              <div className="comparison-half comparison-after">
                <img src="/assets/hdify/enhance-after.webp" alt="Portrait after enhancement" width="1120" height="1400" fetchPriority="high" />
                <span>After</span>
              </div>
              <div className="comparison-divider" aria-hidden="true"><i></i></div>
            </div>
            <div className="privacy-float"><span aria-hidden="true">✦</span> Processed on device</div>
          </div>
        </section>

        <section className="intro section-wrap" id="features">
          <p className="eyebrow">Three focused tools</p>
          <div className="intro-heading">
            <h2>Simple edits.<br />Remarkable difference.</h2>
            <p>No complex timelines or crowded toolbars. Choose a photo, make the fix you need, compare, and save.</p>
          </div>
        </section>

        <section className="feature-grid section-wrap" aria-label="HDify features">
          <article className="feature-card feature-card--enhance">
            <div className="feature-copy"><span>{features[0].number}</span><h3>{features[0].title}</h3><p>{features[0].body}</p></div>
            <div className="feature-image feature-image--portrait"><img src="/assets/hdify/enhance-after.webp" alt="Enhanced portrait result" width="1120" height="1400" loading="lazy" /></div>
          </article>
          <article className="feature-card feature-card--object">
            <div className="feature-copy"><span>{features[1].number}</span><h3>{features[1].title}</h3><p>{features[1].body}</p></div>
            <div className="object-pair">
              <figure><img src="/assets/hdify/remove-object-before.webp" alt="Photo with an unwanted person selected" width="1122" height="1402" loading="lazy" /><figcaption>Original</figcaption></figure>
              <figure><img src="/assets/hdify/remove-object-after.webp" alt="Photo after the person was removed" width="1122" height="1402" loading="lazy" /><figcaption>Clean result</figcaption></figure>
            </div>
          </article>
          <article className="feature-card feature-card--background">
            <div className="feature-copy"><span>{features[2].number}</span><h3>{features[2].title}</h3><p>{features[2].body}</p></div>
            <div className="cutout-stage"><div className="checkerboard"><img src="/assets/hdify/cutout.webp" alt="Subject with a transparent background" width="1122" height="1402" loading="lazy" /></div><div className="color-swatch color-swatch--coral"></div><div className="color-swatch color-swatch--blue"></div></div>
          </article>
        </section>

        <section className="privacy-section section-wrap" id="privacy">
          <div className="privacy-symbol" aria-hidden="true">⌁</div>
          <div className="privacy-copy">
            <p className="eyebrow">Private by design</p>
            <h2>Your photos stay<br />with you.</h2>
            <p>HDify processes photo content locally on your device. Your photos, masks, filenames, and editing results are not uploaded to our servers.</p>
            <p className="privacy-note">Optional, limited app usage and performance data never includes your photos or editing content.</p>
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Read our Privacy Policy <ArrowIcon /></a>
          </div>
          <div className="privacy-points">
            <div><strong>01</strong><span>On-device photo processing</span></div>
            <div><strong>02</strong><span>No account required</span></div>
            <div><strong>03</strong><span>No third-party analytics SDK</span></div>
          </div>
        </section>

        <section className="closing section-wrap">
          <img src="/assets/hdify/app-icon.webp" alt="HDify app icon" width="256" height="256" loading="lazy" />
          <p className="eyebrow">Available for iPhone and iPad</p>
          <h2>A clearer photo<br />is one tap away.</h2>
          <AppStoreButton href={APP_STORE_URL} inverse />
        </section>
      </main>

      <footer className="site-footer section-wrap">
        <div className="footer-brand"><img src="/assets/hdify/app-icon.webp" alt="" width="256" height="256" loading="lazy" /><div><strong>HDify</strong><span>Focused photo fixes, privately.</span></div></div>
        <div className="footer-links" aria-label="Legal and support">
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>
          <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
          <a href={SUPPORT_URL}>Support</a>
        </div>
        <p>© 2026 Fingertip Studio. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
